import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { type PlanChangeRef, toPlanChangeRefs } from "./PlanChangeRepository";
import type { CalendarDayShift } from "./WorkOrderRepository";

const toBit = (value: unknown): boolean =>
	value === true || value === 1 || value === "1" || value === "true";

export type CalendarEventType = "W" | "M" | "D" | "H" | "B";

export interface CalendarEvent {
	EventId: number | null; // null = legacy work order without event (pseudo-event)
	EventTypeCode: CalendarEventType;
	AssetId: number | null; // null = plant-wide (Holiday)
	Start: string;
	End: string;
	Comments: string | null;
	WorkOrderId: number | null;
	WorkOrderNumber: string | null;
	Status: string | null;
	Quantity: number | null;
	Sequence: number | null;
	Notes: string | null;
	PartNumber: string | null;
	ExtendedPartNumber: string | null;
	OperativeShiftId: number | null;
	OperativeDate: string | null;
	OperativeShift: string | null;
	PlanShiftId: number | null;
	PlanDate: string | null;
	PlanShift: string | null;
	IsCarriedOver: boolean;
	IsPulledAhead: boolean;
	StartedAt: string | null;
	CompletedAt: string | null;
	IsSpell: boolean;
	BreakRuleId: number | null;
	BreakLabel: string | null;
	ShiftId: number | null;
}

export const getCalendarEvents = async (
	params: { start: string; end: string; assetId: number },
	signal?: AbortSignal,
): Promise<{ events: CalendarEvent[]; dayShifts: CalendarDayShift[] }> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Start", value: params.start },
		{ name: "End", value: params.end },
		{ name: "AssetId", value: params.assetId },
	];
	const resp = await apiService.callV2(
		"[FAB].[GetCalendarEvents]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching calendar events");
	}

	// BIT/INT can arrive as strings depending on the serializer; the calendar
	// compares these strictly (=== and !flag), so normalize here
	const dayShiftRows = get(
		resp,
		"data.tables[1].rows",
		[],
	) as CalendarDayShift[];
	const eventRows = get(resp, "data.tables[0].rows", []) as CalendarEvent[];

	return {
		events: eventRows.map((r) => ({
			...r,
			IsSpell: toBit(r.IsSpell),
			IsCarriedOver: toBit(r.IsCarriedOver),
			IsPulledAhead: toBit(r.IsPulledAhead),
		})),
		dayShifts: dayShiftRows.map((r) => ({
			...r,
			SchemeAssetId: Number(r.SchemeAssetId),
			ShiftId: Number(r.ShiftId),
			ProductionShift: Number(r.ProductionShift),
			IsOverride: toBit(r.IsOverride),
		})),
	};
};

export interface CalendarEventSaveResult {
	planChanges: PlanChangeRef[];
	workOrderNumber: string | null;
}

export const upsertCalendarEvent = async ({
	eventId = null,
	eventTypeCode,
	assetIds,
	start,
	end,
	comments = null,
	productId = null,
	clipId = null,
	quantity = null,
	notes = null,
	nextLocations = null,
	ship = null,
	isSpell = null,
	workOrderId = null,
	expectedBreakPlanToken = null,
}: {
	eventId?: number | null;
	eventTypeCode: CalendarEventType;
	assetIds: number[] | null;
	start: string;
	end: string;
	comments?: string | null;
	productId?: number | null;
	clipId?: number | null;
	quantity?: number | null;
	notes?: string | null;
	nextLocations?: string | null;
	ship?: string | null;
	isSpell?: boolean | null;
	/** legacy work order without an event — saving materializes it */
	workOrderId?: number | null;
	expectedBreakPlanToken?: string | null;
}): Promise<CalendarEventSaveResult> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{ name: "EventTypeCode", value: eventTypeCode },
		{ name: "AssetIds", value: assetIds?.length ? assetIds.join(",") : null },
		{ name: "Start", value: start },
		{ name: "End", value: end },
		{ name: "Comments", value: comments },
		{ name: "ProductId", value: productId },
		{ name: "ClipId", value: clipId },
		{ name: "Quantity", value: quantity },
		{ name: "Notes", value: notes },
		{ name: "NextLocations", value: nextLocations },
		{ name: "Ship", value: ship },
		{ name: "IsSpell", value: isSpell },
		{ name: "WorkOrderId", value: workOrderId },
	];
	if (eventTypeCode === "B" && expectedBreakPlanToken) {
		parameters.push({
			name: "ExpectedBreakPlanToken",
			value: expectedBreakPlanToken,
		});
	}
	const resp = await apiService.callV2(
		"[FAB].[UpsertCalendarEvent]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving calendar event");
	}

	return {
		planChanges: toPlanChangeRefs(get(resp, "data.tables[1].rows", [])),
		workOrderNumber: get(resp, "data.tables[0].rows[0].WorkOrderNumber", null),
	};
};

export interface BreakPlanRequest {
	eventId: number | null;
	assetIds: number[];
	start: string;
	end: string;
}

export interface BreakEventRequest extends BreakPlanRequest {
	comments?: string | null;
	isSpell?: boolean | null;
}

export interface BreakPlanRow {
	AssetId: number;
	AssetName: string;
	EventId: number;
	IsAnchor: boolean;
	BreakLabel: string;
	OldStart: string | null;
	OldEnd: string | null;
	NewStart: string;
	NewEnd: string;
	OldShift: string | null;
	NewShift: string | null;
	EndShift: string | null;
	CrossesShift: boolean;
	ShiftUnknown: boolean;
	PlanToken: string;
}

export const previewBreakCalendarEvent = async (
	request: BreakPlanRequest,
	signal?: AbortSignal,
): Promise<BreakPlanRow[]> => {
	const response = await new MESApiService().callV2(
		"[FAB].[PreviewBreakCalendarEvent]",
		[
			{ name: "EventId", value: request.eventId },
			{ name: "AssetIds", value: request.assetIds.join(",") },
			{ name: "Start", value: request.start },
			{ name: "End", value: request.end },
		],
		signal,
	);
	if (!response.ok) {
		throw new Error(response.message || "Unable to preview the break schedule");
	}
	const rows = get(response, "data.tables[0].rows", []) as BreakPlanRow[];
	if (!rows.length || !rows[0].PlanToken) {
		throw new Error(
			"The break schedule preview is unavailable. Please try again.",
		);
	}
	return rows.map((row) => ({
		...row,
		AssetId: Number(row.AssetId),
		EventId: Number(row.EventId),
		IsAnchor: toBit(row.IsAnchor),
		CrossesShift: toBit(row.CrossesShift),
		ShiftUnknown: toBit(row.ShiftUnknown),
	}));
};

export const deleteCalendarEvent = async (eventId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "EventId", value: eventId }];
	const resp = await apiService.callV2(
		"[FAB].[DeleteCalendarEvent]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting calendar event");
	}

	// Undo batch created by this delete (none for breaks and plant-wide events)
	return toPlanChangeRefs(get(resp, "data.tables[0].rows", []));
};
